import React, { useEffect, useState, useRef, useCallback, forwardRef } from "react";
import axios from "axios";
import { Row, Col, Dropdown } from "react-bootstrap";
import SearchWithAPI from "../common/search-box-new";
import ReactPaginate from "react-paginate";
import { useNavigate, useLocation } from "react-router-dom";
import { getCookie } from "../getcookie.js";
import { Toast, ToastContainer } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

// Shared font/color utility classes for the table (fs-*, fw-*, text-dkblue, text-ltgrey)
import "../common/Util.css";
import "./invoice.css";

// API Imports
// import { getInvoiceListApi, bulkDeleteInvoiceApi } from "./invoice-api";
import * as invoiceApi from "./invoice-api";
import SearchWithFreeTextAPI from "../common/searchbox-free-text.js";

// SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import CalendarIcon from "../../assets/svg/calendar.svg";
import Plus from "../../assets/svg/add.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
import DeleteModal from "../common/delete-modal.js";
import CommonFilter from "../common/commonfilter.js";
import ImportFileModal from "../common/importfile-modal.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import { sanitizeFileName, validateFileSize } from "../common/security-utils";
import Loader from "../common/Loader.jsx";
import AiChat from "../common/ai-chat.js";
import { askInvoiceChatApi, getInvoiceAiProvidersApi } from "./invoice-api";

// Persist list UI state (search, pagination, date range, filters) across
// navigation to a view page and back. Uses sessionStorage so it survives
// refresh but clears when the browser tab is closed.
const INVOICE_LIST_STATE_KEY = "invoiceListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(INVOICE_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const InvoiceList = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const persisted = loadPersistedState();

  // Filter
  const [showFilter, setShowFilter] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Filters: Status (dropdown), Customer + Order (searchable; Order cascades from Customer)
  const [selectedStatus, setSelectedStatus] = useState(
    persisted.selectedStatus ?? { value: "", label: "All" },
  );
  const [customerFilter, setCustomerFilter] = useState(persisted.customerFilter ?? null);
  const [orderFilter, setOrderFilter] = useState(persisted.orderFilter ?? null);

  // Date range filter (From date - To date), shown as a two-month calendar
  const [startDate, setStartDate] = useState(
    persisted.startDate ? new Date(persisted.startDate) : null,
  );
  const [endDate, setEndDate] = useState(
    persisted.endDate ? new Date(persisted.endDate) : null,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const formatYearMonthDay = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setItemOffset(0);
    if (start && end) {
      setIsDatePickerOpen(false);
    }
  };

  const CalendarCustomInput = forwardRef(({ onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDatePickerOpen((prev) => !prev);
      }}
      title="Filter by Date"
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border: "1px solid #e5e5e5",
        background: startDate ? "#3D4AF5" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
        outline: "none",
      }}
    >
      <img
        src={CalendarIcon}
        alt="Calendar"
        style={{
          width: "18px",
          height: "18px",
          display: "block",
          margin: "auto",
          filter: startDate ? "brightness(0) invert(1)" : "none",
          flexShrink: 0,
        }}
      />
    </button>
  ));

  const statusOption = [
    { value: "", label: "All" },
    { value: "Open", label: "Open" },
    { value: "Sent", label: "Sent" },
    { value: "Paid", label: "Paid" },
    { value: "Partially Paid", label: "Partially Paid" },
    { value: "Due", label: "Due" },
    { value: "Overdue", label: "Overdue" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  // Clear (✕) icon shared by the searchable filters.
  const clearIconStyle = {
    position: "absolute",
    right: "34px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#e5e5e5",
    color: "#555",
    fontSize: "12px",
    lineHeight: "18px",
    textAlign: "center",
    cursor: "pointer",
    zIndex: 5,
    userSelect: "none",
  };

  const options = [
    "Invoice No.",
    "Customer",
    "Order ID",
    "Invoice Date",
    "Due Date",
    "Amount",
    "Outstanding",
    "Status",
  ];

  // Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Navigate
  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Invoice");
  const hasEditPermission = canEdit(rolePermissionList, "Invoice");
  const hasDeletePermission = canDelete(rolePermissionList, "Invoice");
  const hasViewPermission = canView(rolePermissionList, "Invoice");

  // Search
  const [search, setSearch] = useState(persisted.search ?? "");

  // Create Invoice Dropdown
  const [open, setOpen] = useState(false);

  // Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  // Columns with short, fixed-shape values (codes, dates, amounts) get a
  // capped max width so they don't stretch when there's spare row width —
  // Customer/Status stay flexible and absorb the leftover space.
  const COLUMN_MAX_WIDTHS = {
    "Invoice No.": 160,
    "Invoice Date": 160,
    "Due Date": 160,
    "Order ID": 180,
    "Amount": 180,
    "Outstanding": 180,
    "Status": 180,
  };
  const columnsGridTemplate =
    visibleOptions
      .map((label) => {
        if (COLUMN_MAX_WIDTHS[label]) {
          return `minmax(0, ${COLUMN_MAX_WIDTHS[label]}px)`;
        }
        // Customer has no max cap, but shouldn't get squeezed below a
        // readable width when other columns compete for space.
        if (label === "Customer") {
          return "minmax(200px, 1fr)";
        }
        return "minmax(0, 1fr)";
      })
      .join(" ") || "minmax(0, 1fr)";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  // Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setIsBulkDelete(false);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsBulkDelete(false);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      setSelectedRows([]);
      setAllSelected(false);
      getInvoiceList();
    }
  };

  const handleBulkDelete = () => {
    // 🔒 Permission check for delete
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete invoices");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setIsOpenDelete(true);
  };

  // Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // Get Invoice List
  const [invoiceList, setInvoiceList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const getInvoiceList = async (filters = null) => {
    setListLoading(true);
    try {
      const filterParams = [
        selectedStatus?.value
          ? `&status=${encodeURIComponent(selectedStatus.value)}`
          : "",
        customerFilter?.value
          ? `&customer_id=${encodeURIComponent(customerFilter.value)}`
          : "",
        orderFilter?.value
          ? `&order_id=${encodeURIComponent(orderFilter.value)}`
          : "",
        startDate && endDate
          ? `&from_date=${formatYearMonthDay(startDate)}&to_date=${formatYearMonthDay(endDate)}`
          : "",
      ].join("");

      const response = await invoiceApi.getInvoiceListApi(
        `?search=${search}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}${filterParams}${filters ?? ""}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        const formatted = formatInvoiceData(response.data.invoices ?? []);
        setInvoiceList(formatted);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    setSelectedRows([]);
    setAllSelected(false);
    getInvoiceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, itemsPerPage, itemOffset, selectedStatus, customerFilter, orderFilter, startDate, endDate]);

  // Amount is stored pre-formatted (e.g. "₹ 1,00,000.00"), so a plain string
  // compare sorts "10,000" before "1,062" (lexical, not numeric). Strip
  // everything but digits/decimal point and compare as numbers instead.
  const parseAmount = (value) => Number.parseFloat(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0;

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedInvoiceList = React.useMemo(() => {
    if (!sortColumn) return invoiceList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...invoiceList].sort((a, b) => {
      if (sortColumn === "Amount" || sortColumn === "Outstanding") {
        return (parseAmount(a[sortColumn]) - parseAmount(b[sortColumn])) * dir;
      }
      const aStr = (a[sortColumn] ?? "").toString().toLowerCase();
      const bStr = (b[sortColumn] ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [invoiceList, sortColumn, sortDirection]);

  // When the Invoice sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setItemOffset(0);
      setItemsPerPage(10);
      setStartDate(null);
      setEndDate(null);
      setSelectedStatus({ value: "", label: "All" });
      setCustomerFilter(null);
      setOrderFilter(null);
      sessionStorage.removeItem(INVOICE_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        INVOICE_LIST_STATE_KEY,
        JSON.stringify({
          search,
          itemOffset,
          itemsPerPage,
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null,
          selectedStatus,
          customerFilter,
          orderFilter,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, itemOffset, itemsPerPage, startDate, endDate, selectedStatus, customerFilter, orderFilter]);

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Format an amount with the row's currency symbol (falls back to ₹).
  const formatAmount = (value, symbol) => {
    const num = Number.parseFloat(value);
    const display = Number.isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    return `${decodeHtmlEntity(symbol) || "₹"} ${display}`;
  };

  const formatInvoiceData = (rawInvoices) => {
    return rawInvoices?.map((invoice) => ({
      id: invoice.id,
      "Invoice No.": invoice.invoice_number,
      Customer: invoice.customer,
      "Order ID": invoice.order_no || invoice.order_id,
      "Invoice Date": invoice.invoice_date,
      "Due Date": invoice.due_date,
      Amount: formatAmount(invoice.total_amount, invoice.currency_symbol),
      Outstanding: formatAmount(invoice.outstanding_amount, invoice.currency_symbol),
      status: invoice.status,
      "Status": invoice.status,
      is_delete: invoice.is_delete === true || invoice.is_delete === 1 || Boolean(invoice.is_delete),
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    const selectable = invoiceList.filter((invoice) => invoice.is_delete);
    if (selectable.length > 0) {
      setAllSelected(selectable.every((item) => selectedRows.includes(item.id)));
    } else {
      setAllSelected(false);
    }
  }, [selectedRows, invoiceList]);

  // handle checkbox logic
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      const selectableIds = invoiceList
        .filter((invoice) => invoice.is_delete)
        .map((invoice) => invoice.id);
      setSelectedRows(selectableIds);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Import payments (template download / file upload)
  const [importLoading, setImportLoading] = useState(false);

  const downloadBase64Excel = (base64String, filePrefix, fileSizeErrorMessage) => {
    if (!validateFileSize(base64String, 10)) {
      setSnackbarMessage(fileSizeErrorMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return false;
    }

    const fileName = sanitizeFileName(`${filePrefix}_${Date.now()}.xlsx`);
    const binary = atob(base64String);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  const downloadTemplate = async () => {
    try {
      const response = await invoiceApi.downloadInvoicePaymentTemplateApi();
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(response.data.base64, "Invoice_Payment_Template", "File size exceeds 10MB limit");
      }
      setSnackbarMessage(response?.data?.message || "Unable to download template");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
    } catch (err) {
      // Error handled silently
    }
  };

  const uploadInvoicePayments = async (base64String) => {
    if (!base64String || typeof base64String !== "string") {
      setSnackbarMessage("Invalid file data");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setImportLoading(true);
    try {
      const data = { upload_file: base64String };
      const response = await invoiceApi.uploadInvoicePaymentApi(data);

      if (response?.data?.success === true) {
        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Invoice_Payment_Upload_Report", "Report file size exceeds 10MB limit");
        }
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(true);
        handleSnackbarShow();
        setShowImport(false);
        getInvoiceList();
      } else {
        setSnackbarMessage(response?.data?.message || "Upload failed");
        setSnackbarStatus(false);
        handleSnackbarShow();
        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Invoice_Payment_Upload_Error", "Error file size exceeds 10MB limit");
        }
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setImportLoading(false);
    }
  };

  // Copy to clipboard function
  const handleCopyInvoiceNo = (invoiceNo) => {
    navigator.clipboard.writeText(invoiceNo).then(() => {
      setSnackbarMessage(`✓ Copied: ${invoiceNo}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isOpenDelete && (
          <DeleteModal
            deleteUrl={isBulkDelete ? null : `invoice/delete/${deleteId}`}
            bulkDeleteApi={
              isBulkDelete ? invoiceApi.bulkDeleteInvoiceApi : null
            }
            bulkDeleteIds={isBulkDelete ? selectedRows : null}
            onSuccess={onSuccessDelete}
            onCancel={cancelDelete}
          />
        )}
        <ImportFileModal
          type="invoice payments"
          open={showImport}
          onClose={() => setShowImport(false)}
          onExport={downloadTemplate}
          onImport={(base64) => uploadInvoicePayments(base64)}
        />
        <div style={{ marginBottom: "16px" }}>
          <div className="d-flex justify-content-between g-3">
            {/* Invoices count */}
            <div>
              <div
                style={{
                  fontWeight: "500",
                  color: "#172b4d",
                  fontSize: "20px",
                }}
              >
                Invoice - {records}
              </div>
            </div>
            {/* Search box */}
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ flexWrap: "wrap", gap: "12px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "100px",
                  border: "0.8px solid #e5e5e5",
                  padding: "9px 16px",
                  backgroundColor: "#ffffff",
                  height: "40px",
                }}
              >
                <img src={Search} alt="search" style={{ width: "16px" }} />
                <input
                  type="search"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    flex: 1,
                    backgroundColor: "transparent",
                  }}
                  className="no-focus-border"
                />
              </div>

              {/* Status filter */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="d-flex align-items-center justify-content-between"
                  style={{ borderRadius: "12px", padding: "6px 12px", backgroundColor: "#F6F6F6" }}
                >
                  <span className="fs-12 fw-400 text-dkblue">Status :</span>
                  <span className="fs-13 fw-500 textblue px-1">
                    {selectedStatus?.label || "All"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    border: "1px solid #EBEBEB",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusOption.map((option) => (
                    <Dropdown.Item
                      className="fs-14 fw-500"
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setItemOffset(0);
                      }}
                      active={selectedStatus?.value === option.value}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* Customer filter */}
              <div style={{ width: "240px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  onDropdownChange={(option) => {
                    setCustomerFilter(option);
                    setOrderFilter(null); // reset dependent Order filter
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_customers"}
                  placeholder={"Filter by customer"}
                  selectedOption={customerFilter}
                />
                {customerFilter?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomerFilter(null);
                      setOrderFilter(null);
                      setItemOffset(0);
                    }}
                    title="Clear customer filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Order filter — cascades from Customer (uses get_customer_orders) */}
              <div style={{ width: "220px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  key={customerFilter?.value || "no-customer"}
                  onDropdownChange={(option) => {
                    setOrderFilter(option);
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_customer_orders"}
                  params={{ customer_id: customerFilter?.value ?? "" }}
                  placeholder={customerFilter?.value ? "Filter by order" : "Select customer first"}
                  selectedOption={orderFilter}
                  disabled={!customerFilter?.value}
                />
                {orderFilter?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setOrderFilter(null);
                      setItemOffset(0);
                    }}
                    title="Clear order filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Date range filter — two-month calendar */}
              <div className="invoice-date-filter" style={{ width: "40px", height: "40px", flexShrink: 0, position: "relative" }}>
                <DatePicker
                  selectsRange
                  monthsShown={2}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  isClearable
                  customInput={<CalendarCustomInput />}
                  calendarClassName="invoice-date-range-picker"
                  popperPlacement="bottom-end"
                  open={isDatePickerOpen}
                  onClickOutside={() => setIsDatePickerOpen(false)}
                />
              </div>
              {/* <div className="d-flex justify-content-center">
                <img
                  src={Filter}
                  style={{ height: "40px", cursor: "pointer" }}
                  onClick={() => setShowFilter(true)}
                />
              </div> */}
              <div className="d-flex justify-content-end">
                {selectedRows.length > 0 && (
                  <div
                    onClick={handleBulkDelete}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      border: "none",
                      background: "#ff4444",
                      color: "white",
                      height: "40px",
                      fontWeight: "500",
                      cursor: "pointer",
                      width: "fit-content",
                    }}
                  >
                    Delete ({selectedRows.length})
                  </div>
                )}
                {selectedRows.length === 0 && hasCreatePermission && (
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 16px",
                        borderRadius: "100px",
                        borderBottom: "3px solid #1723C2",
                        background:
                          "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                        height: "40px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onClick={() => navigate("/invoice/create")}
                      >
                        <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            color: "#ffffff",
                          }}
                        >
                          Create Invoice
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedRows.length === 0 && (
                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                      marginLeft: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 16px",
                        borderRadius: "100px",
                        border: "1px solid #1723C2",
                        height: "40px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onClick={() => setShowImport(true)}
                      >
                        <img src={ExcelImportIC} style={{ width: "16px" }} alt="Import" />
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            color: "#3D4AF5",
                          }}
                        >
                          Import
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Filter button */}
          </div>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      padding: "10px 10px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={invoiceList.filter((invoice) => invoice.is_delete).length === 0}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                    {visibleOptions.map((label) =>
                      label === "Status" ? (
                        <div
                          key={label}
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <div
                            onClick={() => setShowModal(true)}
                            style={{
                              width: "32px",
                              height: "32px",
                              flexShrink: 0,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <img src={Customize} alt="customize" />
                          </div>
                        </div>
                      ) : (
                        <div
                          key={label}
                          onClick={() => handleSortClick(label)}
                          title={`Sort by ${label}`}
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRight: "1px solid #ebebeb",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                            <div
                              className="fs-12 fw-600 text-ltgrey"
                              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {label.toUpperCase()}
                            </div>
                            <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                              {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                            </span>
                          </div>
                          <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                        </div>
                      ),
                    )}
                  </div>
                </div>
                {/* Data Rows */}
                {listLoading ? (
                  <div style={{ width: "100%", padding: "40px 0" }}>
                    <Loader height="120px" />
                  </div>
                ) : sortedInvoiceList.length === 0 ? (
                  <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                    No invoices found
                  </div>
                ) : (
                  sortedInvoiceList.map((invoice, rowIndex) => (
                  <div
                    key={invoice.id}
                    onMouseEnter={() => setHoveredIndex(rowIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                      backgroundColor: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        padding: "10px 10px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(invoice.id)}
                        onChange={() => toggleRow(invoice.id)}
                        disabled={!anyColumnSelected || !invoice.is_delete}
                      />
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                      onClick={() => {
                        if (hasViewPermission) {
                          navigate("/invoice/view", {
                            state: { id: invoice.id },
                          });
                        }
                      }}
                    >
                      {visibleOptions.map((label) => (
                        <div
                          key={label}
                          title={invoice[label]}
                          className="text-dkblue fs-14 fw-400"
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ebebeb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {invoice[label] || "-"}
                          </div>
                          {(label === "Invoice No." || label === "Order ID") && invoice[label] && (
                            <img
                              src={Copy}
                              alt="Copy"
                              style={{
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                                flexShrink: 0,
                                opacity: 0.6,
                                transition: "opacity 0.2s",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyInvoiceNo(invoice[label]);
                              }}
                              onMouseEnter={(e) => (e.target.style.opacity = "1")}
                              onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
                              title={label === "Order ID" ? "Copy order ID" : "Copy invoice number"}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    {hoveredIndex === rowIndex && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                          {invoice.status === "Open" && hasEditPermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/invoice/create", {
                                  state: { id: invoice.id },
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Edit Invoice"
                            >
                              <EditRow />
                            </div>
                          )}
                          {invoice.is_delete && hasDeletePermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(invoice.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Delete Invoice"
                            >
                              <DeleteRow />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}
                {showModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                        width: "300px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        Select Columns
                      </div>
                      {options.map((label, index) => (
                        <label
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedColumns[label]}
                            onChange={() => toggleColumn(label)}
                            style={{ marginRight: "8px" }}
                          />
                          {label}
                        </label>
                      ))}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          marginTop: "16px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#3D4AF5",
                          color: "#fff",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(event.target.value);
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  previousLabel={
                    <img src={lar} className="ar3" alt="previous" />
                  }
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>
          <ToastContainer position="top-end">
            <Toast
              className="d-inline-block m-1"
              bg={snackbarStatus === true ? "success" : "danger"}
              show={showSnackbar}
              onClose={handleSnackbarClose}
              delay={3000}
              autohide
            >
              <Toast.Body style={{ color: "#ffffff" }}>
                {snackbarMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      <AiChat
        askApi={askInvoiceChatApi}
        providersApi={getInvoiceAiProvidersApi}
        title="Ask about invoices"
        hint="Try: “how many invoices are unpaid?” or “break down invoices by status”"
      />
      </div>
    </div>
  );
};

export default InvoiceList;
